import React from "react";
import { useSupabaseAuth } from "@/context/SupabaseAuthContext";
import { useAuthModal } from "@/context/SupabaseAuthContext";
import UserAvatar from "@/components/UserAvatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  User,
  Map,
  FileText,
  TreePine,
  ArrowLeftRight,
  Mail,
} from "lucide-react";
import { useCurrency } from "@/context/CurrencyContext";
import { getUnreadNotificationCount } from "@/services/notificationService";
import { supabase } from "@/integrations/supabase/client";

const UserMenu: React.FC = () => {
  const { user, signOut } = useSupabaseAuth();
  const { setShowLogin } = useAuthModal();
  const navigate = useNavigate();
  const { currency, toggleCurrency } = useCurrency();
  const [unreadCount, setUnreadCount] = React.useState(0);

  React.useEffect(() => {
    if (!user) return;
    let mounted = true;

    const refresh = async () => {
      const n = await getUnreadNotificationCount(user.id);
      if (mounted) setUnreadCount(n);
    };

    void refresh();

    const channel = supabase
      .channel(`notifications:menu:${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          void refresh();
        },
      )
      .subscribe();

    return () => {
      mounted = false;
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  if (!user) {
    return (
      <div className="flex items-center gap-2">
        <Button
          className="font-semibold"
          variant="ghost"
          onClick={() => navigate("/pricing")}
        >
          Pricing
        </Button>
        <Button className="font-semibold" onClick={() => setShowLogin(true)}>
          Get Started
        </Button>
      </div>
    );
  }

  // Format user's name from email (e.g., "jane.doe@example.com" -> "Jane Doe")
  const formatUserName = (email: string) => {
    const username = email.split("@")[0];
    return username
      .split(/[._-]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Use the full_name from user_metadata, or fallback to formatting the email if it's not available
  const userName =
    user.user_metadata?.full_name || formatUserName(user.email || "");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="flex items-center gap-2 px-2 relative"
        >
          <UserAvatar showDropdown={false} size="m" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-5 h-5 px-1 rounded-full bg-[#40e0d0] text-black text-[10px] font-bold flex items-center justify-center">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1 bg-[#17ebd5]/10 p-2 rounded-md">
            <p className="text-sm font-medium leading-none">{userName}</p>
            <p className="text-xs text-muted-foreground">{user.email}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/profile")}>
          <User className="mr-2 h-4 w-4" />
          <span>Account</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/my-regions")}>
          <Map className="mr-2 h-4 w-4" />
          <span>Bookmarks</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/projects")}>
          <TreePine className="mr-2 h-4 w-4" />
          <span>Projects</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/my-reports")}>
          <FileText className="mr-2 h-4 w-4" />
          <span>Reports</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/messages")}>
          <Mail className="mr-2 h-4 w-4" />
          <span className="flex-1">Messages</span>
          {unreadCount > 0 && (
            <span className="ml-2 text-[10px] font-bold bg-[#40e0d0] text-black rounded-full px-2 py-0.5">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={toggleCurrency}
          className="bg-gray-100 dark:bg-white/10"
        >
          <ArrowLeftRight className="mr-2 h-4 w-4 text-teal-500 font-semibold" />
          <span className="flex-1">
            Prices in {currency === "ZAR" ? "🇺🇸 USD" : "🇿🇦 ZAR"}
          </span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={signOut}>
          <LogOut className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
